import React, {
  useState,
  useRef,
  Fragment,
  ChangeEvent,
  useEffect,
} from "react";
import { Link as RouterLink } from "react-router-dom";
import api from "./api";
import {
  Button,
  Container,
  Stack,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  CssBaseline,
  Box,
  Snackbar,
  Link,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadButtons from "./downloadButtons";
import PdfUsageNote from "./pdfUsageNote";
import { PREMIUM_PROMO_TEXT } from "./billing";
import { SAMPLE_PDF_URL, TranslatedFiles } from "./links";
import { pushDataLayerEvent } from "./analytics";

const Free = () => {
  const inputRef = useRef<any>(null);
  const [showSnack, setShowSnack] = useState(false);
  const [snackSeverity, setSnackSeverity] = useState("error");
  const [snackMessage, setSnackMessage] = useState<any>();
  const [state, setState] = useState(0); // 0: 待機, 1: 処理中, 2: 完了
  const [files, setFiles] = useState<TranslatedFiles>();

  // Weibo シェア関連の state
  const [showWeiboDialog, setShowWeiboDialog] = useState(false);
  const [weiboChallenge, setWeiboChallenge] = useState<{
    challengeId: string;
    message: string;
  } | null>(null);
  const [weiboUrl, setWeiboUrl] = useState("");
  const [weiboVerifying, setWeiboVerifying] = useState(false);
  const [copyTooltip, setCopyTooltip] = useState("复制");

  const sendMessageToBackend = (message: string) => {
    api.post("log?message=" + message);
  };

  // GTM カスタムイベント: 無料版翻訳ページ表示
  useEffect(() => {
    pushDataLayerEvent("viewfree");
  }, []);

  const processFile = async (source: File) => {
    setState(1);
    const params = new FormData();
    params.append("file", source);
    params.append("original_filename", source.name);
    api
      .post("auto_free/", params)
      .then((response) => {
        if (response.status !== 200) {
          setSnackMessage(
            "出现意外错误。您上传的文件可能不符合 Readable 的标准。",
          );
          setSnackSeverity("error");
          setShowSnack(true);
          setState(0);
        } else if ("sizeover" in response.data) {
          setSnackMessage(
            <Fragment>
              文件太大。免费版本只支持少于100页且小于50MB的文件。如果您需要翻译较大的文件，请考虑使用
              <Link
                to="/pricing"
                component={RouterLink}
                onClick={() => sendMessageToBackend("click_sizeover")}
              >
                Readable Pro
              </Link>
              ，成为专业会员。
            </Fragment>,
          );
          setSnackSeverity("error");
          setShowSnack(true);
          setState(0);
          sendMessageToBackend("show_sizeover");
        } else if ("undefined" in response.data) {
          setState(0);
        } else if ("error" in response.data) {
          setSnackMessage(
            "出现意外错误。您上传的文件可能不符合 Readable 的标准。",
          );
          setSnackSeverity("error");
          setShowSnack(true);
          setState(0);
        } else if ("notext" in response.data) {
          setSnackMessage(
            "未检测到文本。请尝试在PDF阅读器中打开该文件，拖动鼠标确认是否有可以选中的文本。您可以尝试对文件进行分割，如果上传的文件中没有识别到文本，则Readable无法进行翻译。您也可以尝试寻找与该文件内容相同的其他PDF版本。",
          );
          setSnackSeverity("error");
          setShowSnack(true);
          setState(0);
        } else if ("notpdf" in response.data) {
          setSnackMessage("仅支持 PDF 文件。");
          setSnackSeverity("error");
          setShowSnack(true);
          setState(0);
        } else {
          setFiles({
            ja: response.data.ja,
            alt: response.data.alt,
            print: response.data.print,
          });
          setState(2);
          // GTM カスタムイベント: 無料翻訳成功
          pushDataLayerEvent("autofree");
        }
      })
      .catch((e) => {
        if (e.response?.status === 429) {
          // クォータ超過またはレート制限
          const data = e.response.data;
          if (data.error === "quota_exceeded") {
            // Weibo シェアダイアログを表示するためチャレンジを開始
            handleStartWeiboChallenge();
            setSnackMessage(
              <Fragment>
                本月免费次数已用完（已使用 {data.used}/{data.limit + data.bonus}{" "}
                次）。您可以通过分享微博获得额外翻译次数，或考虑使用
                <Link
                  to="/pricing"
                  component={RouterLink}
                  onClick={() => sendMessageToBackend("click_quota_exceeded")}
                >
                  Readable Pro
                </Link>
                。
              </Fragment>,
            );
          } else if (data.error === "rate_limited") {
            setSnackMessage(
              `请求过于频繁，请在 ${data.retryAfterSec} 秒后重试。`,
            );
          } else {
            setSnackMessage("请求受限，请稍后重试。");
          }
          setSnackSeverity("warning");
          setShowSnack(true);
          setState(0);
          sendMessageToBackend("show_quota_exceeded");
        } else if (e.response?.status === 413) {
          setSnackMessage(
            <Fragment>
              文件太大。免费版本只支持少于100页且小于50MB的文件。如果您需要翻译较大的文件，请考虑使用
              <Link
                to="/pricing"
                component={RouterLink}
                onClick={() => sendMessageToBackend("click_sizeover")}
              >
                Readable Pro
              </Link>
              ，成为专业会员。
            </Fragment>,
          );
          setSnackSeverity("error");
          setShowSnack(true);
          setState(0);
          sendMessageToBackend("show_sizeover");
        } else {
          setSnackMessage(
            "出现意外错误。您上传的文件可能不符合 Readable 的标准。",
          );
          setSnackSeverity("error");
          setShowSnack(true);
          setState(0);
        }
      });
  };

  // Weibo チャレンジを開始
  const handleStartWeiboChallenge = async () => {
    try {
      const response = await api.post("weibo/challenge");
      setWeiboChallenge({
        challengeId: response.data.challengeId,
        message: response.data.message,
      });
      setWeiboUrl("");
      setShowWeiboDialog(true);
      sendMessageToBackend("weibo_challenge_started");
    } catch (e) {
      setSnackMessage("无法开始微博验证，请稍后重试。");
      setSnackSeverity("error");
      setShowSnack(true);
    }
  };

  // Weibo 投稿を検証
  const handleVerifyWeibo = async () => {
    if (!weiboChallenge || !weiboUrl) return;

    setWeiboVerifying(true);
    try {
      const response = await api.post("weibo/verify", {
        challengeId: weiboChallenge.challengeId,
        weiboUrl: weiboUrl,
      });
      setShowWeiboDialog(false);
      setSnackMessage(response.data.message);
      setSnackSeverity("success");
      setShowSnack(true);
      sendMessageToBackend("weibo_verify_success");
      // GTM カスタムイベント: Weibo シェア成功
      pushDataLayerEvent("weiboshare");
    } catch (e: any) {
      const message = e.response?.data?.message || "验证失败，请重试。";
      setSnackMessage(message);
      setSnackSeverity("error");
      setShowSnack(true);
      sendMessageToBackend("weibo_verify_failed");
    } finally {
      setWeiboVerifying(false);
    }
  };

  // Weibo メッセージをクリップボードにコピー
  const handleCopyWeiboMessage = async () => {
    if (!weiboChallenge?.message) return;
    try {
      await navigator.clipboard.writeText(weiboChallenge.message);
      setCopyTooltip("已复制！");
      setTimeout(() => setCopyTooltip("复制"), 2000);
      sendMessageToBackend("weibo_message_copied");
    } catch {
      setSnackMessage("复制失败，请手动复制。");
      setSnackSeverity("error");
      setShowSnack(true);
    }
  };

  // Weibo 投稿ページを開く
  const handleOpenWeiboShare = () => {
    if (!weiboChallenge?.message) return;
    const shareText = encodeURIComponent(weiboChallenge.message);
    const weiboShareUrl = `https://service.weibo.com/share/share.php?title=${shareText}`;
    window.open(weiboShareUrl, "_blank");
    sendMessageToBackend("weibo_share_opened");
  };

  const onDrop = (e: DragEvent) => {
    e.stopPropagation();
    e.preventDefault();
    // @ts-ignore
    const files = e.dataTransfer.files;
    if ((state === 0 || state >= 2) && files.length === 1 && files[0]) {
      processFile(files[0]);
    }
  };

  const onDragOver = (e: DragEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const onFileInputChange = async (e: ChangeEvent<HTMLInputElement>) => {
    // @ts-ignore
    const source = e.target.files[0];
    if (!source) {
      return;
    }
    processFile(source);
  };

  return (
    <Fragment>
      {/* @ts-ignore */}
      <Container
        component="main"
        sx={{ mb: 2 }}
        maxWidth="lg"
        onDragOver={onDragOver}
        onDrop={onDrop}
      >
        <CssBaseline />
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            fontWeight="fontWeightBold"
            sx={{ mb: 2 }}
          >
            1. 请上传需要翻译的PDF文件。
          </Typography>
          <img src="./imgs/upload.png" alt="上传 PDF 文件" width="200" />
          {(state === 0 || state >= 2) && (
            <Button
              variant="contained"
              sx={{ mt: 2, mb: 2, width: 200, height: 40 }}
              onClick={() => {
                inputRef.current.click();
              }}
            >
              上传 PDF 文件
            </Button>
          )}
          {(state === 0 || state >= 2) && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              也可以直接将文件拖拽到此页面
            </Typography>
          )}
          {(state === 0 || state >= 2) && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              没有合适的文件？试试
              <Link href={SAMPLE_PDF_URL} target="_blank" rel="noopener">
                示例文件
              </Link>
            </Typography>
          )}
          {state === 1 && (
            <CircularProgress sx={{ mt: 2, mb: 4, height: 40 }} />
          )}
          <Typography
            variant="h4"
            component="h1"
            fontWeight="fontWeightBold"
            sx={{ mb: 4 }}
          >
            2. 下载翻译结果
          </Typography>
          {state < 2 && (
            <img
              src="./imgs/download_disable.png"
              alt="下载翻译结果"
              width="300"
            />
          )}
          {state >= 2 && (
            <img src="./imgs/download.png" alt="下载翻译结果" width="300" />
          )}
          <DownloadButtons files={state >= 2 ? files : undefined} />
          <PdfUsageNote />
          <Paper
            variant="outlined"
            sx={{ mt: 8, pt: 1, pb: 1, pl: 2, pr: 2, textAlign: "center" }}
          >
            <Link to="/pricing" component={RouterLink}>
              专业会员
            </Link>
            可享受无限次翻译
            <br />
            <Typography sx={{ fontWeight: "bold", color: "#1976d2" }}>
              {PREMIUM_PROMO_TEXT}
            </Typography>
          </Paper>
        </Box>
      </Container>
      <input
        hidden
        type="file"
        onChange={onFileInputChange}
        accept={
          /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
            ? undefined
            : ".pdf"
        }
        ref={inputRef}
      />
      <Snackbar
        open={showSnack}
        autoHideDuration={20000}
        onClose={() => setShowSnack(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSnack(false)}
          // @ts-ignore
          severity={snackSeverity}
          sx={{ width: "100%" }}
        >
          {snackMessage}
        </Alert>
      </Snackbar>

      {/* Weibo シェアダイアログ */}
      <Dialog
        open={showWeiboDialog}
        onClose={() => setShowWeiboDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>分享微博获取额外翻译次数</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            在微博上分享 Readable，即可获得 3 次额外翻译次数！
          </Typography>
          <Typography sx={{ mb: 2, fontWeight: "bold" }}>步骤：</Typography>
          <Typography component="div" sx={{ mb: 2 }}>
            <ol style={{ paddingLeft: 20, margin: 0 }}>
              <li>
                点击下方按钮复制内容或直接前往微博发布（可以修改，但需包含链接）
              </li>
              <li>发布后，将微博链接粘贴到下方输入框</li>
              <li>点击"验证"按钮完成验证</li>
            </ol>
          </Typography>
          <Paper
            variant="outlined"
            sx={{ p: 2, mb: 2, backgroundColor: "#f5f5f5" }}
          >
            <Typography sx={{ fontStyle: "italic" }}>
              {weiboChallenge?.message}
            </Typography>
          </Paper>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Tooltip title={copyTooltip}>
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyWeiboMessage}
                size="small"
              >
                复制内容
              </Button>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<OpenInNewIcon />}
              onClick={handleOpenWeiboShare}
              size="small"
              color="error"
            >
              前往微博发布
            </Button>
          </Stack>
          <TextField
            fullWidth
            label="请粘贴您的微博链接"
            placeholder="https://weibo.com/..."
            value={weiboUrl}
            onChange={(e) => setWeiboUrl(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowWeiboDialog(false)}>取消</Button>
          <Button
            onClick={handleVerifyWeibo}
            variant="contained"
            disabled={!weiboUrl || weiboVerifying}
          >
            {weiboVerifying ? <CircularProgress size={24} /> : "验证"}
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default Free;
